import React from "react";
import { Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

import { InvestmentChartData } from "libLegacy/api/models";
import LoadingDots from "components/libComponents/LoadingDots/LoadingDots";
import { VictoryLine, VictoryAxis, VictoryChart } from "victory";

import { AXIS_STYLE, INVESTMENTS_CHART_PADDING } from "./chartStyles";
import { ptBR } from "date-fns/locale";
import { ChartCaption } from "./ChartCaption";

const useStyles = makeStyles()((theme) => ({
  outer: {
    height: "100%",
    position: "relative",
    alignContent: "baseline",
  },
  overlay: {
    position: "absolute",
    height: "100%",
    width: "100%",
  },
  chartContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  legend: {
    gap: theme.spacing(2),
    fontSize: "12px",
    position: "absolute",
    bottom: 0,
  },
}));

const formatYLabel = (value: string) => {
  const formattedValue = (+value * 100).toFixed(0);
  return `${formattedValue}%`;
};

const formatYValue = ({ value }) => +value;

const formatXLabel = (xValue) => {
  const refDate = new Date(xValue);
  const month = refDate
    .toLocaleDateString(ptBR.code, { month: "short" })
    .replace(".", "");
  const year = refDate.toLocaleDateString(ptBR.code, { year: "2-digit" });

  return `${month} ${year}`;
};

export const ChartProfitability = ({
  estimatedProfitability,
  realProfitability,
  loading,
}: ChartProfitabilityProps) => {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <Grid container item className={classes.outer}>
      <Typography variant="body1">Rentabilidade</Typography>
      <div className={classes.chartContainer}>
        <VictoryChart padding={INVESTMENTS_CHART_PADDING}>
          <VictoryAxis
            dependentAxis
            tickFormat={formatYLabel}
            style={AXIS_STYLE}
            fixLabelOverlap
          />
          <VictoryAxis
            style={AXIS_STYLE}
            tickFormat={formatXLabel}
            fixLabelOverlap
          />
          {/* ESTIMATED PROFITABILITY */}
          {estimatedProfitability && (
            <VictoryLine
              style={{
                data: {
                  stroke: theme.palette.charts.primary,
                  strokeWidth: "3px",
                },
              }}
              data={estimatedProfitability}
              x="time"
              y={formatYValue}
            />
          )}
          {/* REAL PROFITABILITY */}
          {realProfitability && (
            <VictoryLine
              style={{
                data: {
                  stroke: theme.palette.charts.secondary,
                  strokeWidth: "3px",
                },
              }}
              data={realProfitability}
              x="time"
              y={formatYValue}
            />
          )}
        </VictoryChart>
        <Grid container className={classes.legend} justifyContent="center">
          <ChartCaption label="Real" color={theme.palette.charts.secondary} />
          <ChartCaption label="Estimada" color={theme.palette.charts.primary} />
        </Grid>
      </div>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        className={classes.overlay}
      >
        {loading && <LoadingDots />}
      </Grid>
    </Grid>
  );
};

export type ChartProfitabilityProps = {
  estimatedProfitability?: Array<InvestmentChartData>;
  realProfitability?: Array<InvestmentChartData>;
  loading: boolean;
};
